import { NextPage } from "next";
import FAQItem from "@/components/ui/FAQItem";
import Space from "@/components/ui/Space";
import { FAQPageJsonLd, NextSeo } from "next-seo";
import { useRouter } from "next/router";

export async function getServerSideProps({ locale }) {
  return {
    props: {
      locale,
      title: locale === "fa" ? "سؤالات متداول | درسون" : "FAQ | Darsoon",
      description:
        locale === "fa"
          ? "در این بخش می‌توانید سوالات متداول در مورد خدمات درسون را مشاهده کنید و پاسخ‌های مورد نیاز خود را پیدا کنید."
          : "Explore our FAQ section to get answers to your questions about Darsoon",
    },
  };
}

type Props = {
  title: string;
  description: string;
  locale: string;
};

const Page: NextPage = ({ title, description, locale }: Props) => {
  const router = useRouter();
  const baseUrl = "https://darsoon.com";
  const canonicalUrl = `${baseUrl}${locale === "fa" ? "" : "/en"}${router.asPath}`;

  const faqItems = [
    {
      question: "What is Darsoon?",
      answer:
        "Darsoon is a marketplace for private online tutoring. We provide a medium where tutors can present themselves through their profile's pages, and students can select their preferred tutors.",
    },
    {
      question: "Who are the founders of Darsoon?",
      answer:
        "We are a few families living in Toronto, Canada who have created this website. We took online classes for our children during the pandemic and realized how useful these classes are. Even though these classes were online and remote, they were even better than in-person classes in several aspects. That's why we decided to create this website so that more people can take advantage of these services.",
    },
    {
      question: "How do you screen the tutors?",
      answer:
        "The first group of the tutors are those who have been our own or our children's tutors and we were very satisfied with their qualifications and teaching skills. We now have a very robust selection process, where we review the resumes, and interview selected candidates. Once they pass all our criteria, they are added to the website.",
    },
    {
      question: "How do I find a tutor and take lessons in Darsoon?",
      answer:
        "You first select your preferred tutor on the Darsoon website and complete the introductory meeting request form. We will give your contact information to the tutor and ask him/her to contact you to schedule the introductory meeting. The tutor may contact you by WhatsApp or email. In the introductory meeting, which is free and lasts around 15 minutes, you and the tutor will talk about your expectations and the teaching method. Once you both agree that there is a perfect match, you can go ahead and make payments on the tutor's profile page on Darsoon website. The cost listed on the website is for when you purchase the 5 or 12-session package. If you purchase a single-session package the cost is 15% higher, whereas you receive a 5% discount if you purchase a 20-session package. You can schedule your sessions directly with the tutor once you make the payment.",
    },
    {
      question: "What is the free introductory meeting?",
      answer:
        "A free introductory meeting is a short 15-minute video call that gives you and the tutor a chance to get to know each other better. During this session, you can discuss the tutor's teaching style, your own needs, and anything else that is important to you. After that, you can make a more informed decision about whether or not to purchase a package to start classes.In this session, the tutor will not be teaching. It is simply a chance to get to know the tutor.",
    },
    {
      question: "What age group is Darsoon for?",
      answer:
        "Darsoon's students include children of five years of age and above to adults and seniors. On each tutor's profile page, you can see what age group the lessons are suitable for. After you select a tutor for your children, you can also make sure that the lessons are suitable for them during the introductory session.",
    },
    {
      question: "What are the cancellation rules?",
      answer:
        "You can change the time of your class up to 24 hours before the start of the class by contacting the tutor and coordinating with him/her.",
    },
    {
      question: "How should I pay for the lessons?",
      answer:
        "You can pay online by credit card using the secure Square gateway on our website. On the Payment page, you can select the package you would like to purchase, and then make the payment. If you are a resident of Canada, you can also pay by e-transfer. Please contact us if you would like to use this method of payment.",
    },
    {
      question: "How do you set the price of the lessons?",
      answer:
        "The price of the lessons is set by the tutors. Darsoon only provides a price range which is the same for the tutors who teach a subject.",
    },
    {
      question: "Can I coordinate with my tutor and pay the lesson fee directly to him/her?",
      answer: "Direct payment to the tutors is not permitted as it creates accounting problems for Darsoon.",
    },
    {
      question: "Which video platform is used to hold the lessons?",
      answer: "Tutors and students can agree on their platform of choice. Darsoon recommends Skype or Google Meet.",
    },
    {
      question: "Can I record the sessions for myself?",
      answer: "No. Neither the tutor nor the student is allowed to record the sessions.",
    },
    {
      question: "The subject I'm looking for doesn't exist on Darsoon's website. What can I do?",
      answer:
        "We would appreciate it if you let us know through the Contact Us page. We try to add new subjects regularly based on our students' requests.",
    },
    {
      question:
        "The duration of the session on the tutor's profile page is one hour, but I prefer half-hour sessions. What can I do?",
      answer:
        "You can discuss this issue with the tutor in the introductory meeting and mutually agree on changing the duration of the session. The cost of the lesson will be adjusted proportionally.",
    },
    {
      question: "Is it possible to hold semi-private lessons with more than one student?",
      answer:
        "You can discuss this issue with the tutor during the introductory meeting. The cost will increase by 20% for each additional student.",
    },
    {
      question:
        "Is there an evaluation session before starting the lessons? Is it possible for me to speak with the tutor to make sure the lesson is a fit for my needs?",
      answer:
        "Yes. The introductory meeting is free and lasts for 15 minutes so that you can make sure that the lesson is suitable. In this meeting, the tutor can also do an evaluation and determine your level.",
    },
    {
      question: "Can the price of lessons change while I am going through a package?",
      answer:
        "Darsoon reserves the right to change the price of the lessons. However, after you pay for a multi-session package, if the price changes, it will not have any impact on the price of the sessions that have been already paid by you.",
    },
  ];

  const faqItemsFa = [
    {
      question: "درسون چه کار می‌کند؟",
      answer:
        "درسون فضایی برای تدریس خصوصی آنلاین است. ما رسانه‌ای را ارائه می‌دهیم که معلمان می‌توانند خود را از طریق صفحات پروفایل خود نشان دهند و شاگردان می‌توانند معلمان مورد نظر خود را انتخاب کنند.",
    },
    {
      question: "بنیانگذاران درسون چه کسانی هستند؟",
      answer:
        "ما چند خانواده‌ ساکن تورنتوی کانادا هستیم که این وب‌سایت را درست کرده‌ایم. ما در مدت قرنطینه‌ی کرونایی برای فرزندانمان کلاس آنلاین گرفتیم و متوجه شدیم که چقدر این کلاس‌ها با این که آنلاین و از راه دور هستند، مفید هستند و حتی از خیلی از جنبه‌ها بهتر از کلاس‌های حضوری هستند. برای همین تصمیم گرفتیم کاری کنیم که بتوانیم خانواده‌های بیشتری از این مساله استفاده ببرند و از طرفی فرصتی برای عزیزان داخل کشور فراهم کنیم که بتوانند دانش و تجربه‌ی خودشان را با هم‌وطنانشان در خارج از کشور نیز به اشتراک بگذارند.",
    },
    {
      question: "معلمهای درسون چگونه انتخاب شده‌اند؟",
      answer:
        "حلقه‌ی اولیه‌ی معلمهای درسون، کسانی هستند که معلم خودمان یا فرزندانمان بوده‌اند و ما از از صلاحیت‌ها و مهارت‌های تدریسشان بسیار راضی بودیم. ما اکنون یک فرآیند انتخاب بسیار سخت‌گیرانه داریم که در آن رزومه‌ها را بررسی و با گزینه‌های منتخب مصاحبه می‌کنیم. اگر آنها تمام معیارهای مورد نظر ما را داشته باشند، به وب‌سایت اضافه می‌شوند.",
    },
    {
      question: "چگونه معلم مورد نظرم را انتخاب کنم و با او در درسون کلاس بگیرم؟",
      answer:
        "شما ابتدا معلم خود را در وب‌سایت درسون انتخاب می‌کنید و فرم درخواست جلسه‌ی معرفی را کامل می‌کنید. ما اطلاعات تماس شما را به معلم می‌دهیم و از او می‌خواهیم که با شما تماس بگیرد و زمان جلسه‌ی معرفی را هماهنگ کند. روش تماس معلم با شما، واتساپ یا ایمیل است. در جلسه‌ی معرفی که رایگان و مدت آن حدود ۱۵ دقیقه است، شما و معلم درباره‌ی انتظاراتتان از کلاس و نحوه‌ی تدریس صحبت می‌کنید. زمانی که هر دو به توافق رسیدید، می‌توانید پرداخت را در صفحه پروفایل معلم در وب‌سایت درسون انجام دهید. هزینه‌ی درج شده در وب‌سایت مربوط به زمانی است که شما بسته‌ی ۵ یا ۱۲ جلسه‌ای را تهیه کنید. هزینه‌ی تک‌جلسه ۱۵% بیشتر از هزینه‌ی درج شده در وب‌سایت و هزینه‌ی بسته ۲۰ جلسه‌ای ۵% کمتر است. شما می‌توانید زمان جلسات بعدی را با معلم به طور مستقیم هماهنگ کنید.",
    },
    {
      question: "منظور از جلسه معرفی رایگان چیست؟",
      answer:
        "جلسه معرفی رایگان یک جلسه گفتگوی تصویری کوتاه حدود ۱۵ دقیقه‌ای است که فرصتی برای شما و معلم فراهم می‌کند تا بیشتر با یکدیگر آشنا شوید. در این جلسه می‌توانید در مورد شیوه تدریس معلم، شرایط خودتان و هر موضوع دیگری که برایتان مهم است صحبت کنید. بعد از آن، شما می‌توانید با اطلاعات کامل‌تر تصمیم بگیرید که آیا برای شروع کلاس، پکیجی را خریداری کنید یا خیر. در این جلسه معلم تدریس نمی‌کند و صرفاً یک جلسه آشنایی با معلم خواهید داشت.",
    },
    {
      question: "کلاسهای درسون برای چه گروه سنی مناسب است؟",
      answer:
        "به طور کلی شاگردان درسون می‌توانند از کودک (پنج سال به بالا)‌ تا بزرگسال باشند. در صفحه‌ی شخصی هر معلم می‌توانید ببینید که کلاس آن معلم برای چه گروه یا گروه‌های سنی‌ مناسب است. اگر کلاس را برای فرزندانتان انتخاب می‌کنید، می‌توانید در جلسه‌ی معرفی از مناسب بودن کلاس برای فرزندتان اطمینان یابید.",
    },
    {
      question: "قوانین تغییر زمان یا لغو کلاس به چه صورت هستند؟",
      answer: "شما تا ۲۴ ساعت قبل از شروع کلاس می‌توانید با تماس با معلم و هماهنگی با وی زمان کلاس را تغییر دهید.",
    },
    {
      question: "هزینه‌ی کلاس را چگونه باید پرداخت کنم؟",
      answer:
        "شما می‌توانید با کردیت کارت از طریق درگاه پرداخت امن Square مبلغ را پرداخت کنید. در صفحه پرداخت، می‌توانید بسته مورد نظر خود را برای خرید انتخاب کنید و سپس پرداخت را انجام دهید. اگر مقیم کانادا هستید، می‌توانید از طریق e-transfer نیز پرداخت کنید. در صورت تمایل به استفاده از این روش پرداخت لطفا با ما تماس بگیرید.",
    },
    {
      question: "هزینه کلاس ها چطور تعیین می‌شود؟",
      answer:
        "هزینه کلاس‌ها توسط معلمین تعیین می‌شود. درسون فقط یک بازه قیمت پیشنهاد می‌دهد که برای معلمانی که یک درس را تدریس می‌کنند یکسان است.",
    },
    {
      question: "آیا من می‌توانم خودم با معلم هماهنگ کنم و هزینه‌ی کلاس را مستقیم به حساب بانکی او پرداخت کنم؟",
      answer: "پرداخت مستقیم به معلمان مجاز نمی‌باشد زیرا باعث ایجاد مشکلات حسابداری برای درسون می‌شود.",
    },
    {
      question: "کلاس‌ها به چه زبانی برگزار می‌شوند؟",
      answer:
        "زبان کلاس‌ها بستگی به توافق شاگرد و معلم دارد و ما توصیه می‌کنیم شما این موضوع را در جلسه‌ی معرفی مطرح کنید. معلمانی که به زبان انگلیسی تسلط یا آشنایی دارند، این مطلب را در صفحه‌ی شخصی‌شان نوشته‌اند. ما و معلمان به چالش‌های آموزش به کودکان و نوجوانان نسل دومی که به زبان فارسی تسلط ندارند آگاه هستیم. زبان این کلاس‌ها می‌تواند ترکیبی از انگلیسی و فارسی باشد.",
    },
    {
      question: "تفاوت وب‌سایت شما با وب‌سایت‌های مشابهی که در داخل کشور هستند چیست؟",
      answer:
        "ما به تفاوت‌های آموزش به عزیزان ساکن خارج از کشور به ویژه نسل دومی‌ها واقف هستیم و معلمان را در جریان این مسائل گذاشته‌ایم. ما قبل از راه‌اندازی درسون، خود کلاس‌های آنلاین زیادی را با معلمان داخل کشور داشته‌ایم و در این وب‌سایت سعی کرده‌ایم از تجربیات خود بهره برده تا کلاس‌های آنلاین به بهترین گونه برگزار شود.",
    },
    {
      question: "کلاس‌های آنلاین در چه پلتفرمی انجام می‌شود؟",
      answer:
        "معلم و شاگرد می‌توانند هر یک از پلتفرم‌ها را با توافق طرفین استفاده کنند. پیشنهاد درسون استفاده از Google meet یا Skype است.",
    },
    {
      question: "آیا می‌توانم جلسات را برای خودم ضبط کنم؟",
      answer: "خیر. نه معلم و نه شاگرد اجازه ندارند جلسات را ضبط کنند.",
    },
    {
      question: "کلاسی که من دنبالش هستم در سایت درسون وجود ندارد. چه می‌توانم بکنم؟",
      answer:
        "اگر از طریق صفحه «تماس با ما» به ما اطلاع دهید خوشحال خواهیم شد. ما سعی می‌کنیم کلاس‌های جدید را به طور منظم بر اساس درخواست شاگردان خود اضافه کنیم.",
    },
    {
      question: "مدت زمان جلسه در صفحه معلم یک ساعت است اما من جلسات نیم ساعته را ترجیح می‌دهم. چه می‌توانم بکنم؟",
      answer:
        "شما می‌توانید این موضوع را در جلسه معارفه با معلم در میان بگذارید و در مورد تغییر مدت جلسه با یکدیگر توافق کنید. هزینه کلاس نیز متناسب با زمان جدید جلسات تعدیل خواهد شد.",
    },
    {
      question: "آیا امکان برگزاری کلاس های نیمه خصوصی با بیش از یک شاگرد وجود دارد؟",
      answer:
        "می‌توانید در جلسه معرفی این موضوع را با معلم در میان بگذارید. هزینه‌ی جلسات برای هر شاگرد اضافی ۲۰ درصد افزایش می‌یابد.",
    },
    {
      question:
        "آیا قبل از شروع کلاس‌ها جلسه تعیین سطح برگزار می‌شود؟ آیا می‌توانم با معلم صحبت کنم تا مطمئن شوم این کلاس متناسب با نیازهای من است؟",
      answer:
        "بله. جلسه معرفی رایگان و ۱۵ دقیقه است تا از مناسب بودن کلاس مطمئن شوید. در این جلسه معلم نیز می‌تواند تعیین سطح را انجام دهد و سطح شما را مشخص کند.",
    },
    {
      question: "آیا در حین گذراندن یک پکیج قیمت دروس تغییر می‌کند؟",
      answer:
        "حق تغییر قیمت کلاس‌ها برای درسون محفوظ است. اما پس از پرداخت هزینه‌ی بسته‌ی چند جلسه‌ای، در صورت تغییر قیمت، تاثیری در قیمت جلساتی که قبلا توسط شما پرداخت شده است، نخواهد داشت.",
    },
  ];

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalUrl}
        openGraph={{
          title: title,
          description: description,
          url: canonicalUrl,
          locale: locale === "fa" ? "fa_IR" : "en_US",
          site_name: locale === "fa" ? "درسون" : "Darsoon",
          type: "website",
        }}
        additionalLinkTags={[
          {
            rel: "alternate",
            href: `${baseUrl}${router.asPath}`,
            hrefLang: "fa",
          },
          {
            rel: "alternate",
            href: `${baseUrl}/en${router.asPath}`,
            hrefLang: "en",
          },
          {
            rel: "alternate",
            href: `${baseUrl}${router.asPath}`,
            hrefLang: "x-default",
          },
        ]}
      />
      <FAQPageJsonLd
        mainEntity={
          locale === "fa"
            ? faqItemsFa.map((item) => ({
                "@type": "Question",
                name: item.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: item.answer,
                },
              }))
            : faqItems.map((item) => ({
                "@type": "Question",
                name: item.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: item.answer,
                },
              }))
        }
      />
      <div className="mx-auto max-w-70rem ">
        <h1 className="H1  mb-4">{locale === "fa" ? "پرسش‌های پرتکرار" : "FAQ"}</h1>
        <Space size="h-12" />
        {locale === "fa"
          ? faqItemsFa.map((item, index) => (
              <FAQItem key={index} number={index + 1} question={item.question}>
                <p>{item.answer}</p>
              </FAQItem>
            ))
          : faqItems.map((item, index) => (
              <FAQItem key={index} number={index + 1} question={item.question}>
                <p>{item.answer}</p>
              </FAQItem>
            ))}
      </div>
    </>
  );
};

export default Page;
